import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { Avatar } from "@/components/shared/Avatar";

function PillRow({ g, value, counts, onPick }) {
  return (
    <div className="flex items-center gap-2 flex-wrap px-[18px] py-3">
      <span className="font-heading font-extrabold text-[11px] uppercase tracking-[.1em] text-muted-foreground mr-1">{g.label}</span>
      {g.options.map((o) => {
        const on = value === o.value;
        const n = counts?.[o.value];
        return (
          <button key={o.value} type="button" onClick={() => onPick(g.key, o.value)} data-testid={`fp-${g.key}-${o.value}`}
            className={`h-9 px-3.5 rounded-full text-sm inline-flex items-center gap-1.5 transition-colors ${
              on ? "bg-brand text-white font-bold" : "bg-card border border-border text-foreground"}`}>
            {o.label}{n != null && <span className={`tabular-nums ${on ? "" : "text-muted-foreground"}`}>{n}</span>}
          </button>
        );
      })}
    </div>
  );
}

// Nezávislé přepínače (multi-výběr) — vzhled jako pilulky (zadání 113).
function ToggleRow({ g, values, onPick }) {
  return (
    <div className="flex items-center gap-2 flex-wrap px-[18px] py-3">
      <span className="font-heading font-extrabold text-[11px] uppercase tracking-[.1em] text-muted-foreground mr-1">{g.label}</span>
      {g.options.map((o) => {
        const on = !!values[o.value];
        return (
          <button key={o.value} type="button" onClick={() => onPick(o.value, !on)} data-testid={`fp-${g.key}-${o.value}`}
            className={`min-h-[42px] h-9 px-3.5 rounded-full text-sm transition-colors ${on ? "bg-brand text-white font-bold" : "bg-card border border-border text-foreground"}`}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Dvojice datových polí Od | Do (zadání 113).
function RangeRow({ g, values, onPick }) {
  return (
    <div className="flex items-center gap-3 flex-wrap px-[18px] py-3">
      <span className="font-heading font-extrabold text-[11px] uppercase tracking-[.1em] text-muted-foreground">{g.label}</span>
      <input type="date" value={values[g.fromKey] || ""} onChange={(e) => onPick(g.fromKey, e.target.value)} data-testid={`fp-${g.key}-from`}
        className="h-11 min-w-0 flex-1 bg-card border border-border rounded-[12px] px-3 text-sm" />
      <span className="text-muted-foreground">–</span>
      <input type="date" value={values[g.toKey] || ""} onChange={(e) => onPick(g.toKey, e.target.value)} data-testid={`fp-${g.key}-to`}
        className="h-11 min-w-0 flex-1 bg-card border border-border rounded-[12px] px-3 text-sm" />
    </div>
  );
}

function ListCard({ g, value, counts, onPick }) {
  const total = counts?.[g.clearValue ?? "all"];
  return (
    <div className="bg-card border border-border rounded-[16px] overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/60">
        <span className="font-heading font-extrabold text-[11px] uppercase tracking-[.1em] text-muted-foreground">{g.label}</span>
        {total != null && <span className="text-xs text-muted-foreground/70 tabular-nums">{total}</span>}
      </div>
      <div className="max-h-[236px] overflow-y-auto p-1.5">
        {g.options.map((o) => {
          const on = value === o.value;
          const n = counts?.[o.value];
          return (
            <button key={o.value} type="button" onClick={() => onPick(g.key, o.value)} data-testid={`fp-${g.key}-${o.value}`}
              className={`w-full min-h-[42px] px-2.5 rounded-[10px] text-[14.5px] text-left inline-flex items-center gap-2 transition-colors ${
                on ? "bg-brand/10 text-brand font-bold" : "font-semibold hover:bg-brand/[.07]"}`}>
              {g.avatar && o.value !== (g.clearValue ?? "all") && <Avatar name={o.label} photo={o.photo} size={26} className="bg-brand/15 text-brand" />}
              <span className="truncate">{o.label}</span>
              {n != null && <span className="ml-auto tabular-nums text-sm text-muted-foreground">{n}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Vícevýběr se zaškrtávátky, počty a volitelným hledáním (zadání 157/D —
// Protistrana, Způsob). Vybrané drží rodič jako objekt {hodnota: true}, klik
// přepíná přes onPick(g.key, hodnota).
function CheckList({ g, selected, counts, onPick }) {
  const [q, setQ] = useState("");
  const s = q.trim().toLowerCase();
  const opts = s ? g.options.filter((o) => o.label.toLowerCase().includes(s)) : g.options;
  return (
    <div className="bg-card border border-border rounded-[16px] overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/60">
        <span className="font-heading font-extrabold text-[11px] uppercase tracking-[.1em] text-muted-foreground">{g.label}</span>
      </div>
      {g.searchable && (
        <div className="p-2 border-b border-border/60">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat…" data-testid={`fp-${g.key}-search`}
            className="h-9 w-full bg-secondary rounded-[10px] px-3 text-sm focus:outline-none" />
        </div>
      )}
      <div className="max-h-[236px] overflow-y-auto p-1.5">
        {opts.map((o) => {
          const on = !!selected[o.value];
          const n = counts?.[o.value];
          return (
            <button key={o.value} type="button" onClick={() => onPick(g.key, o.value)} data-testid={`fp-${g.key}-${o.value}`}
              className={`w-full min-h-[42px] px-2.5 rounded-[10px] text-[14.5px] text-left inline-flex items-center gap-2 transition-colors ${
                on ? "bg-brand/10 text-brand font-bold" : "font-semibold hover:bg-brand/[.07]"}`}>
              <span className={`h-4 w-4 rounded-[5px] border grid place-items-center shrink-0 ${on ? "bg-brand border-brand text-white" : "border-border"}`}>{on && <Check size={12} />}</span>
              <span className="truncate">{o.label}</span>
              {n != null && <span className="ml-auto tabular-nums text-sm text-muted-foreground">{n}</span>}
            </button>
          );
        })}
        {opts.length === 0 && <p className="px-2.5 py-3 text-sm text-muted-foreground">Nic nenalezeno.</p>}
      </div>
    </div>
  );
}

// Karta filtrů (desktop) i spodní šuplík (mobil) — zadání 108/§2–§3. Teplý
// podklad, bílé seznamy. Filtr se aplikuje hned při kliku; „Zobrazit" jen zavírá.
export function FilterPanel({ open, onClose, groups, values, counts = {}, setValue, clear, filterCount, resultCount, plural, mobile, width = 660, top = null }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open || mobile) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest?.("[data-testid=filters-btn]")) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); window.removeEventListener("keydown", onKey); };
  }, [open, mobile, onClose]);

  const body = (
    <>
      {/* Volitelný první řádek nad skupinami (např. rozsah Moje/Zadal na mobilu, zadání 191) */}
      {top}
      {groups.filter((g) => g.layout === "pills").map((g) => <PillRow key={g.key} g={g} value={values[g.key]} counts={counts[g.key]} onPick={setValue} />)}
      {groups.some((g) => g.layout === "list") && (
        <div className={`grid ${mobile ? "grid-cols-1" : "grid-cols-2"} gap-3 px-[18px] pb-1`}>
          {groups.filter((g) => g.layout === "list").map((g) => <ListCard key={g.key} g={g} value={values[g.key]} counts={counts[g.key]} onPick={setValue} />)}
        </div>
      )}
      {groups.filter((g) => g.layout === "range").map((g) => <RangeRow key={g.key} g={g} values={values} onPick={setValue} />)}
      {groups.some((g) => g.layout === "checklist") && (
        <div className={`grid ${mobile ? "grid-cols-1" : "grid-cols-2"} gap-3 px-[18px] pb-1`}>
          {groups.filter((g) => g.layout === "checklist").map((g) => <CheckList key={g.key} g={g} selected={values[g.key] || {}} counts={counts[g.key]} onPick={setValue} />)}
        </div>
      )}
      {groups.filter((g) => g.layout === "toggles").map((g) => <ToggleRow key={g.key} g={g} values={values} onPick={setValue} />)}
    </>
  );
  const footer = (
    <div className={`flex items-center gap-2.5 px-[18px] py-3.5 border-t border-border/80 ${mobile ? "pb-[max(60px,calc(0.75rem+env(safe-area-inset-bottom)))]" : ""}`}>
      <button type="button" onClick={filterCount ? clear : undefined} data-testid="fp-clear"
        className={`font-semibold ${filterCount ? "text-brand" : "text-muted-foreground/70"}`}>Vymazat filtry</button>
      <button type="button" onClick={onClose} data-testid="fp-apply"
        className="ml-auto h-11 px-5 bg-brand text-white rounded-[14px] font-bold">{`Zobrazit ${resultCount} ${plural(resultCount)}`}</button>
    </div>
  );

  if (mobile) {
    return (
      <>
        <div onClick={onClose} className={`fixed inset-0 z-40 bg-black/45 transition-opacity motion-reduce:transition-none ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} />
        <div data-testid="filter-panel"
          className={`fixed inset-x-0 bottom-0 z-50 bg-background rounded-t-[24px] max-h-[88%] overflow-y-auto transition-transform duration-300 motion-reduce:transition-none [transition-timing-function:cubic-bezier(.32,.72,0,1)] ${open ? "translate-y-0" : "translate-y-[104%]"}`}>
          <div className="pt-3">{body}{footer}</div>
        </div>
      </>
    );
  }
  if (!open) return null;
  // In-flow karta (oprava 109/d): obsah pod pruhem uhne, panel nikdy nepřetéká
  // přes seznam. Lehký reveal přes animaci; klik mimo / Escape zavírá.
  return (
    <div ref={ref} data-testid="filter-panel" style={{ width }}
      className="mt-2 max-w-full bg-background border border-border rounded-[20px] shadow-[0_22px_50px_rgba(0,0,0,.18)] origin-top animate-in fade-in slide-in-from-top-1 duration-200">
      <div className="pt-2">{body}{footer}</div>
    </div>
  );
}
